import React, { useEffect, useState } from 'react';
import { getUserCrops, getEstimationMeetData } from '../firebase/firestoreService';
import { Pie } from 'react-chartjs-2';
import namemapping from '../utils/namemapping'; // Import the swapped mapping
import { useNavigate } from 'react-router-dom'; // Import useNavigate hook

// Reusable StatCard Component
const StatCard = ({ title, value }) => (
  <div style={styles.statCard}>
    <div style={styles.statTitle}>{title}</div>
    <div style={styles.statValue}>{value}</div>
  </div>
);

// Reusable Table Row for each crop
const CropTableRow = ({ cropData, index, email }) => {
  const { cropName } = cropData;
  const { demand, met } = cropData.estimationData;

  const needToGet = (demand - met).toFixed(3);

  const cropPieChartData = {
    labels: ['Met', 'Not Met'],
    datasets: [
      {
        data: [((met / demand) * 100).toFixed(2), ((1 - met / demand) * 100).toFixed(2)],
        backgroundColor: ['#27ae60', '#f39c12'], // Green for met, Orange for unmet
        hoverOffset: 4,
      },
    ],
  };

  const displayName = namemapping[cropName] || cropName.toUpperCase();

  const navigate = useNavigate();  // Initialize useNavigate hook

  // Function to handle button click and navigate to add route
  const handleSowingClick = () => {
    navigate(`/add`, { state: { email, cropName } });  // Navigate to "/add" with email and cropName as state
  };

  return (
    <tr
      key={cropName}
      style={index % 2 === 0 ? styles.tableRowEven : styles.tableRowOdd}
    >
      <td style={styles.td}>{displayName}</td>
      <td style={styles.td}>{demand.toFixed(3)}</td>
      <td style={styles.td}>{met.toFixed(3)}</td>
      <td style={styles.td}>{needToGet}</td>
      <td style={styles.td}>
        <div style={styles.pieChartWrapper}>
          <Pie
            data={cropPieChartData}
            options={{
              responsive: true,
              plugins: { title: { display: false, text: false }, legend: { display: false } },
            }}
          />
        </div>
      </td>
      {/* New Action Column with button */}
      <td style={styles.td}>
        <button onClick={handleSowingClick} style={styles.actionButton}>
          Sowing the Crop
        </button>
      </td>
    </tr>
  );
};

const RecommandBasedOnKnownCrops = ({ email }) => {
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortOrder, setSortOrder] = useState('desc'); // 'desc' or 'asc'

  useEffect(() => {
    const fetchData = async () => {
      try {
        const knownCrops = await getUserCrops(email);
        const estimationData = await getEstimationMeetData();

        const sortedEstimationData = Object.entries(estimationData)
          .sort((a, b) => {
            if (sortOrder === 'desc') {
              return b[1].met - a[1].met; // Sort by 'met' descending
            } else {
              return a[1].met - b[1].met; // Sort by 'met' ascending
            }
          })
          .map(([cropName, data]) => ({ cropName, ...data }));

        const filteredRecommendations = sortedEstimationData.filter(item =>
          knownCrops.some(knownCrop => knownCrop.toLowerCase() === item.cropName.toLowerCase())
        );

        setRecommendations(filteredRecommendations);
        setLoading(false);
      } catch (err) {
        setError('Error fetching data: ' + err.message);
        setLoading(false);
      }
    };

    fetchData();
  }, [email, sortOrder]);

  const toggleSortOrder = () => {
    setSortOrder(prevOrder => (prevOrder === 'desc' ? 'asc' : 'desc'));
  };

  if (loading) return <loading />;

  if (error) {
    return <div style={styles.error}>{error}</div>;
  }

  return (
    <div style={styles.container}>
      <h1 style={styles.header}>Recommended Crops Based on Known Crops</h1>

      <div style={styles.statsContainer}>
        <StatCard title="Total Recommendations" value={recommendations.length} />
      </div>

      <div style={styles.sortButtonWrapper}>
        <button onClick={toggleSortOrder} style={styles.button}>
          Sort by Met ({sortOrder === 'desc' ? 'Descending' : 'Ascending'})
        </button>
      </div>

      <div style={styles.tableWrapper}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Crop Name</th>
              <th style={styles.th}>Demand</th>
              <th style={styles.th}>Met</th>
              <th style={styles.th}>Need</th>
              <th style={styles.th}>Demand Met Breakdown</th>
              <th style={styles.th}>Action</th> {/* New column header */}
            </tr>
          </thead>
          <tbody>
            {recommendations.map((item, index) => (
              <CropTableRow
                cropData={{
                  cropName: item.cropName,
                  estimationData: { demand: item.demand, met: item.met }
                }}
                index={index}
                key={item.cropName}
                email={email}  // Pass email as prop
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const styles = {
  container: {
    margin: '40px auto',
    padding: '20px',
    fontFamily: 'Arial, sans-serif',
    borderRadius: '12px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    width: '90%',
    backgroundColor: 'rgba(255, 255, 255, 0.3)',
    border: '2px solid rgba(255, 255, 255, 0.9)',
  },
  header: {
    fontSize: '24px',
    fontWeight: '600',
    padding: '0px 0px 10px',
    textAlign: 'center',
    color: '#ffff',
  },
  statsContainer: {
    display: 'flex',
    justifyContent: 'space-evenly',
    marginBottom: '20px',
    gap: '20px',
  },
  statCard: {
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    padding: '15px',
    borderRadius: '8px',
    boxShadow: '0 2px 5px rgba(0, 0, 0, 0.4)',
    textAlign: 'center',
    width: '180px',
  },
  statTitle: {
    fontSize: '16px',
    fontWeight: '500',
    color: '#555',
  },
  statValue: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#2d3d4c',
  },
  tableWrapper: {
    overflowX: 'auto',
    borderRadius: '12px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    borderRadius: '12px',
    tableLayout: 'fixed',
  },
  th: {
    padding: '8px 16px',
    backgroundColor: '#4CAF50',
    color: '#fff',
    textAlign: 'left',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: '14px',
  },
  td: {
    padding: '8px 16px',
    textAlign: 'left',
    borderBottom: '1px solid #ddd',
    fontSize: '14px',
  },
  tableRowEven: {
    backgroundColor: '#f9f9f9',
  },
  tableRowOdd: {
    backgroundColor: '#ffffff',
  },
  pieChartWrapper: {
    width: '80px',
    height: '80px',
    margin: '0 auto',
  },
  sortButtonWrapper: {
    textAlign: 'center',
    marginBottom: '20px',
  },
  button: {
    padding: '10px 18px',
    fontSize: '14px',
    backgroundColor: '#4CAF50',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
  },
  actionButton: {
    padding: '8px 16px',
    fontSize: '14px',
    backgroundColor: '#3498db',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    textAlign: 'center',
  },
  loading: {
    fontSize: '16px',
    textAlign: 'center',
    marginTop: '20px',
  },
  error: {
    color: 'red',
    fontWeight: 'bold',
    textAlign: 'center',
    marginTop: '20px',
  },
};

export default RecommandBasedOnKnownCrops;
